import { nanoid } from "nanoid";
import { z } from "zod";
import {
  nanoidSchema,
  scalarSet,
  slugSchema,
  timestampSchema,
  unixTimestamp
} from "../utility/index.js";
import { defaultWebsite, websiteSchema } from "../project/website.js";
import { activeDeploymentSchema } from "../project/active-deployment.js";
import { defaultVersion, versionSchema } from "../project/version/version.js";
const logoSchema = z.object({
  darkMode: z.string(),
  lightMode: z.string()
});
const sharedProjectSchema = z.object({
  /** Unique identifier for the project */
  uid: nanoidSchema,
  /** Name of the project */
  name: z.string(),
  /** Record of the last successful deployment */
  activeDeployment: activeDeploymentSchema.nullable().default(null),
  /** Track last published timestamp to allow build interrupts */
  lastPublished: timestampSchema.nullable().default(null),
  /** Uid of the most recent publish record (failed or successful) */
  lastPublishedUid: z.string().nullable().default(null),
  /** Uid of the login portal customization object for private projects */
  loginPortalUid: z.string().default(""),
  /** Active theme id for the project */
  activeThemeId: z.string().default(""),
  /** Typesense id for the project */
  typesenseId: z.number().optional(),
  /** Private projects require a login from a user in an access group */
  isPrivate: z.boolean().default(false),
  /** Set of allowed access groups */
  accessGroups: scalarSet(z.set(nanoidSchema).max(50)).default([]),
  /** Timestamp when the project was created */
  createdAt: timestampSchema.default(unixTimestamp),
  /** Timestamp when the project was last updated */
  updatedAt: timestampSchema.default(unixTimestamp)
});
const projectSchema = sharedProjectSchema.extend({
  /** Versions of the project */
  versions: z.record(z.string(), versionSchema),
  /** Active template id for the project */
  activeTemplateId: z.string().optional().default(""),
  /** Active version id for the project */
  activeVersionId: z.string().optional().default(""),
  /** Logo for the project */
  logo: logoSchema,
  /** Deployed site configuration for a project */
  website: websiteSchema.describe(
    "Deployed site configuration for a project"
  )
}).transform((project) => {
  if (Object.keys(project.versions).length === 0) {
    const v = defaultVersion({});
    project.versions[v.uid] = v;
  }
  if (!project.activeVersionId) {
    project.activeVersionId = Object.keys(project.versions)[0];
  }
  return project;
});
function defaultProject(initialState = {}) {
  const v = defaultVersion({});
  return projectSchema.parse({
    uid: nanoid(),
    name: "",
    activeTemplateId: "",
    activeThemeId: "",
    activeVersionId: v.uid,
    createdAt: unixTimestamp(),
    updatedAt: unixTimestamp(),
    header: [],
    logo: {
      darkMode: "",
      lightMode: ""
    },
    versions: {
      [v.uid]: v
    },
    website: defaultWebsite(),
    activeDeployment: null,
    lastPublishedUid: null,
    loginPortalUid: "",
    ...initialState
  });
}
const githubProjectSchema = sharedProjectSchema.extend({
  uid: nanoidSchema,
  slug: slugSchema.optional(),
  /** Last publish status info */
  publishStatus: z.string().default(""),
  /** Last publish output message */
  publishMessage: z.string().default(""),
  /**
   *
   * Reference info for the link repository
   *
   * Optional for projects which are not linked
   */
  repository: z.object({
    linkedBy: z.string(),
    id: z.number(),
    name: z.string().min(2),
    configPath: z.string().default(""),
    branch: z.string().default(""),
    publishOnMerge: z.boolean().default(false),
    publishPreviews: z.boolean().default(false),
    prComments: z.boolean().default(false),
    /** One-time repository expired tracking */
    expired: z.boolean().default(false)
  }).nullable().optional()
});
function isGithubProject(project) {
  return "publishStatus" in project;
}
export {
  defaultProject,
  githubProjectSchema,
  isGithubProject,
  logoSchema,
  projectSchema,
  sharedProjectSchema
};
//# sourceMappingURL=project.js.map
